import React, { Component } from 'react';
import { RefreshCw, AlertTriangle, WifiOff, Database } from 'lucide-react';
import { isIndexedDBError, analyzeIndexedDBError, attemptIndexedDBRecovery } from '../../utils/indexedDbErrorHandler';

interface Props {
  children: React.ReactNode;
  fallbackComponent?: React.ComponentType<{ error: Error; retry: () => void }>;
}

interface State {
  hasError: boolean;
  error?: Error;
  isRecovering: boolean;
  recoveryAttempts: number;
}

/**
 * Error boundary specifically designed to handle database connection issues,
 * including IndexedDB errors, Firestore connectivity problems, and network failures.
 */
export class DatabaseErrorBoundary extends Component<Props, State> {
  private maxRecoveryAttempts = 3;
  
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      isRecovering: false,
      recoveryAttempts: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    console.error('Database Error Boundary caught an error:', error, errorInfo);
    
    // Check if it's a database-related error
    const isDatabaseError = this.isDatabaseRelatedError(error);
    
    if (isDatabaseError) {
      console.error('Database-related error detected:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        isIndexedDB: isIndexedDBError(error)
      });
      
      // Send to Sentry with database error context
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          tags: {
            errorBoundary: 'database',
            errorType: 'database_connection',
            isIndexedDB: isIndexedDBError(error).toString()
          }
        });
      }
    }
  }

  /**
   * Determines if an error is related to database connectivity issues
   */
  private isDatabaseRelatedError = (error: Error): boolean => {
    const errorMessage = error.message.toLowerCase();
    const errorName = error.name.toLowerCase();
    
    // Check for IndexedDB errors
    if (isIndexedDBError(error)) {
      return true;
    }
    
    // Check for Firebase/Firestore errors
    const firestorePatterns = [
      'firestore',
      'firebase',
      'connection failed',
      'network error',
      'quota exceeded',
      'permission denied',
      'unavailable'
    ];
    
    // Check for general database connection patterns
    const databasePatterns = [
      'database',
      'connection lost',
      'server lost',
      'offline',
      'network',
      'fetch failed',
      'load failed'
    ];
    
    return [...firestorePatterns, ...databasePatterns].some(pattern => 
      errorMessage.includes(pattern) || errorName.includes(pattern)
    );
  };

  /**
   * Attempts to recover from database errors
   */
  private handleRetry = async () => {
    if (this.state.recoveryAttempts >= this.maxRecoveryAttempts) {
      // Max attempts reached, force page refresh
      window.location.reload();
      return;
    }

    this.setState({ isRecovering: true });

    try {
      let recovered = false;
      
      // If it's an IndexedDB error, try specific recovery
      if (this.state.error && isIndexedDBError(this.state.error)) {
        recovered = await attemptIndexedDBRecovery();
      } else {
        // For other database errors, wait a bit and try again
        await new Promise(resolve => setTimeout(resolve, 2000));
        recovered = true;
      }
      
      if (recovered) {
        this.setState({ 
          hasError: false, 
          error: undefined, 
          isRecovering: false,
          recoveryAttempts: this.state.recoveryAttempts + 1
        });
      } else {
        throw new Error('Recovery failed');
      }
    } catch (recoveryError) {
      console.error('Recovery attempt failed:', recoveryError);
      this.setState({ 
        isRecovering: false,
        recoveryAttempts: this.state.recoveryAttempts + 1
      });
      
      // If we've tried multiple times, suggest page refresh
      if (this.state.recoveryAttempts >= this.maxRecoveryAttempts - 1) {
        setTimeout(() => {
          if (confirm('Recovery attempts failed. Would you like to refresh the page?')) {
            window.location.reload();
          }
        }, 1000);
      }
    }
  };

  /**
   * Forces a page refresh
   */
  private handleRefresh = () => {
    window.location.reload();
  };

  /**
   * Renders the error UI based on the error type
   */
  private renderErrorUI = () => {
    const { error, isRecovering, recoveryAttempts } = this.state;
    
    if (!error) return null;

    // Use custom fallback component if provided
    if (this.props.fallbackComponent) {
      const FallbackComponent = this.props.fallbackComponent;
      return <FallbackComponent error={error} retry={this.handleRetry} />;
    }

    // Analyze IndexedDB errors specifically
    const isIndexedDB = isIndexedDBError(error);
    const indexedDBInfo = isIndexedDB ? analyzeIndexedDBError(error) : null;
    
    // Determine error type and messaging
    let icon = <AlertTriangle className="w-12 h-12 text-red-500" />;
    let title = 'Database Connection Error';
    let message = 'There was a problem connecting to the database. This might be temporary.';
    let canRetry = true;
    
    if (isIndexedDB && indexedDBInfo) {
      icon = <Database className="w-12 h-12 text-red-500" />;
      title = 'Database Storage Issue';
      message = indexedDBInfo.userMessage;
      canRetry = indexedDBInfo.canRetry && recoveryAttempts < this.maxRecoveryAttempts;
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      icon = <WifiOff className="w-12 h-12 text-red-500" />;
      title = 'Network Connection Error';
      message = 'Unable to connect to our services. Please check your internet connection.';
    } else if (error.message.includes('firestore') || error.message.includes('firebase')) {
      icon = <Database className="w-12 h-12 text-orange-500" />;
      title = 'Service Connection Error';
      message = 'Unable to connect to our database services. This may be temporary.';
    }

    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-md mx-auto">
            <div className="flex justify-center mb-4">
              {icon}
            </div>
            
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              {title}
            </h2>
            
            <p className="text-red-600 mb-6">
              {message}
            </p>
            
            {recoveryAttempts > 0 && (
              <p className="text-sm text-red-500 mb-4">
                Recovery attempts: {recoveryAttempts}/{this.maxRecoveryAttempts}
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {canRetry && (
                <button
                  onClick={this.handleRetry}
                  disabled={isRecovering}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRecovering ? 'animate-spin' : ''}`} />
                  {isRecovering ? 'Recovering...' : 'Try Again'}
                </button>
              )}
              
              <button
                onClick={this.handleRefresh}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-red-700 font-medium text-sm">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-100 p-3 rounded overflow-auto max-h-32">
                  {error.stack || error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  };

  render() {
    if (this.state.hasError) {
      return this.renderErrorUI();
    }

    return this.props.children;
  }
} 